import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { researchAPI } from '../lib/api';
import { chunkedUpload, shouldUseChunkedUpload } from '../lib/chunkedUpload';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Trash2, Play, Loader2, CheckCircle,
  FileText, Zap, Copy, User, Video, Clock, ChevronDown, ChevronUp,
  Lightbulb, Image, X, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { cn } from '../lib/utils';

const statusConfig = {
  pending: { label: 'Upload Videos', color: 'bg-zinc-100 text-zinc-600' },
  analyzing: { label: 'Analyzing...', color: 'bg-blue-50 text-blue-700' },
  analyzed: { label: 'Ready', color: 'bg-emerald-50 text-emerald-700' },
};

export default function InfluencerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inf, setInf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [converting, setConverting] = useState(false);
  const [script, setScript] = useState('');
  const [convertedResult, setConvertedResult] = useState(null);
  const [showTranscripts, setShowTranscripts] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showConvertHistory, setShowConvertHistory] = useState(false);
  const [showGenHistory, setShowGenHistory] = useState(false);
  const fileInputRef = useRef(null);

  // Idea → Script state
  const [showIdeaDialog, setShowIdeaDialog] = useState(false);
  const [ideaText, setIdeaText] = useState('');
  const [ideaImages, setIdeaImages] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generatedResult, setGeneratedResult] = useState(null);
  const ideaImageRef = useRef(null);

  const loadInf = useCallback(async () => {
    try {
      setLoading(true);
      const res = await researchAPI.getInfluencer(id);
      setInf(res.data);
      if (res.data.status === 'analyzing') {
        setTimeout(() => loadInf(), 10000);
      }
    } catch {
      toast.error('Failed to load');
      navigate('/research');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadInf(); }, [loadInf]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 10 - (inf?.videos?.length || 0);
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    let ok = 0;
    for (let fi = 0; fi < toUpload.length; fi++) {
      const file = toUpload[fi];
      try {
        setUploadProgress(0);
        if (shouldUseChunkedUpload(file)) {
          await chunkedUpload(file, 'influencer_video', id, (p) => setUploadProgress(p));
        } else {
          const fd = new FormData();
          fd.append('file', file);
          await researchAPI.uploadVideo(id, fd);
        }
        ok++;
      } catch (err) {
        toast.error(`Failed: ${file.name}`);
      }
    }
    if (ok > 0) { toast.success(`${ok} video${ok > 1 ? 's' : ''} uploaded`); loadInf(); }
    setUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteVideo = async (vidId) => {
    try { await researchAPI.deleteVideo(id, vidId); toast.success('Removed'); loadInf(); }
    catch { toast.error('Failed'); }
  };

  const handleAnalyze = async () => {
    try {
      setAnalyzing(true);
      setInf(prev => ({ ...prev, status: 'analyzing' }));
      const res = await researchAPI.analyze(id);
      toast.success(`Analyzed ${res.data.successful} transcripts`);
      loadInf();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
      loadInf();
    } finally { setAnalyzing(false); }
  };

  const handleConvert = async () => {
    if (!script.trim()) { toast.error('Enter your script first'); return; }
    try {
      setConverting(true);
      const res = await researchAPI.convertScript(id, script.trim());
      setConvertedResult(res.data);
      toast.success('Script converted!');
      loadInf();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Conversion failed');
    } finally { setConverting(false); }
  };

  // Idea → Script
  const handleAddIdeaImages = (e) => {
    const files = Array.from(e.target.files || []);
    setIdeaImages(prev => [...prev, ...files].slice(0, 5));
    if (ideaImageRef.current) ideaImageRef.current.value = '';
  };

  const removeIdeaImage = (idx) => {
    setIdeaImages(prev => prev.filter((_, i) => i !== idx));
  };

  const handleGenerateScript = async () => {
    if (!ideaText.trim()) { toast.error('Enter your idea first'); return; }
    try {
      setGenerating(true);
      const fd = new FormData();
      fd.append('idea', ideaText.trim());
      ideaImages.forEach(img => fd.append('images', img));
      const res = await researchAPI.generateScript(id, fd);
      setGeneratedResult(res.data);
      toast.success('Script generated!');
      setShowIdeaDialog(false);
      setIdeaText('');
      setIdeaImages([]);
      loadInf();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    return bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
  };

  if (loading && !inf) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>;
  }
  if (!inf) return null;

  const st = statusConfig[inf.status] || statusConfig.pending;
  const isAnalyzed = inf.status === 'analyzed';
  const isAnalyzing = inf.status === 'analyzing' || analyzing;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="influencer-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/research')} data-testid="back-to-research">
          <ArrowLeft size={18} />
        </Button>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 bg-zinc-100 rounded-full flex items-center justify-center">
            <User size={18} className="text-zinc-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">{inf.name}</h1>
            {inf.handle && <p className="text-sm text-zinc-400">@{inf.handle}</p>}
          </div>
        </div>
        <Badge className={st.color}>{st.label}</Badge>
      </div>

      {/* Step 1: Upload Videos */}
      <Section title="Step 1: Upload Videos" subtitle={`${inf.videos?.length || 0}/10 videos uploaded`} icon={<Video size={16} />}>
        <input type="file" ref={fileInputRef} className="hidden" accept="video/*" multiple onChange={handleUpload} />
        <div className="flex items-center gap-3 mb-4">
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading || (inf.videos?.length || 0) >= 10} className="text-zinc-700 border-zinc-200" data-testid="upload-videos-btn">
            <Upload size={14} className="mr-1.5" /> {uploading ? (uploadProgress > 0 ? `Uploading ${uploadProgress}%` : 'Uploading...') : 'Upload Videos'}
          </Button>
          <span className="text-xs text-zinc-400">Upload the influencer's top performing videos (up to 10)</span>
        </div>
        {inf.videos?.length > 0 && (
          <div className="space-y-2">
            {inf.videos.map((v, i) => {
              const transcript = inf.transcripts?.find(t => t.video_id === v.id);
              return (
                <div key={v.id} className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-md px-4 py-2.5">
                  <div className="w-6 h-6 bg-zinc-200 rounded flex items-center justify-center text-xs text-zinc-500 font-medium shrink-0">{i + 1}</div>
                  <Play size={14} className="text-zinc-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-800 truncate">{v.original_name}</p>
                    <p className="text-xs text-zinc-400">{formatSize(v.file_size)}</p>
                  </div>
                  {transcript && (
                    <Badge variant="outline" className={cn("text-xs shrink-0", transcript.text?.startsWith('[') ? "text-red-600 border-red-200" : "text-emerald-600 border-emerald-200")}>
                      {transcript.text?.startsWith('[') ? 'Failed' : 'Transcribed'}
                    </Badge>
                  )}
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-red-600 shrink-0" onClick={() => handleDeleteVideo(v.id)} data-testid={`delete-video-${v.id}`}>
                    <Trash2 size={13} />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Step 2: Analyze */}
      <Section title="Step 2: Analyze Style & Storytelling" subtitle="Transcribe videos, extract style patterns, ideas, and storytelling approach" icon={<Zap size={16} />}>
        <div className="flex items-center gap-3">
          <Button onClick={handleAnalyze} disabled={isAnalyzing || !inf.videos?.length} className={cn("text-white", isAnalyzed ? "bg-zinc-700 hover:bg-zinc-600" : "bg-zinc-900 hover:bg-zinc-800")} data-testid="analyze-btn">
            {isAnalyzing ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Analyzing...</> : isAnalyzed ? <><CheckCircle size={14} className="mr-1.5" /> Re-analyze</> : <><Zap size={14} className="mr-1.5" /> Analyze Now</>}
          </Button>
          <span className="text-xs text-zinc-400">
            {isAnalyzing ? 'Transcribing, analyzing style, ideas & storytelling... this may take a few minutes' : isAnalyzed ? 'Full analysis complete — style, ideas, storytelling, virality patterns' : `Upload videos first (${inf.videos?.length || 0} ready)`}
          </span>
        </div>

        {inf.transcripts?.length > 0 && (
          <div className="mt-4">
            <button onClick={() => setShowTranscripts(!showTranscripts)} className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="toggle-transcripts">
              {showTranscripts ? <ChevronUp size={14} /> : <ChevronDown size={14} />} View Transcripts ({inf.transcripts.length})
            </button>
            {showTranscripts && (
              <div className="mt-3 space-y-3 max-h-96 overflow-y-auto">
                {inf.transcripts.map((t, i) => (
                  <div key={i} className="bg-zinc-50 border border-zinc-200 rounded-md p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-zinc-600">Video {i + 1}: {t.name}</span>
                      {!t.text?.startsWith('[') && <Button variant="ghost" size="sm" className="h-6 text-xs text-zinc-400" onClick={() => copyToClipboard(t.text)}><Copy size={12} className="mr-1" /> Copy</Button>}
                    </div>
                    <p className={cn("text-xs leading-relaxed whitespace-pre-wrap", t.text?.startsWith('[') ? "text-red-500" : "text-zinc-700")}>{t.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {inf.style_analysis && (
          <div className="mt-4">
            <button onClick={() => setShowAnalysis(!showAnalysis)} className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="toggle-analysis">
              {showAnalysis ? <ChevronUp size={14} /> : <ChevronDown size={14} />} View Full Analysis (Style + Ideas + Storytelling)
            </button>
            {showAnalysis && (
              <div className="mt-3 bg-zinc-50 border border-zinc-200 rounded-md p-4">
                <div className="flex justify-end mb-2">
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-zinc-400" onClick={() => copyToClipboard(inf.style_analysis)}><Copy size={12} className="mr-1" /> Copy</Button>
                </div>
                <div className="text-xs text-zinc-700 leading-relaxed whitespace-pre-wrap max-h-96 overflow-y-auto">{inf.style_analysis}</div>
              </div>
            )}
          </div>
        )}
      </Section>

      {/* Step 3: Use — Convert Script or Generate from Idea */}
      {isAnalyzed && (
        <Section title="Step 3: Create Content" subtitle={`Use ${inf.name}'s style to convert scripts or generate new ones from ideas`} icon={<Sparkles size={16} />}>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Convert existing script */}
            <div className="border border-zinc-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-zinc-500" />
                <h4 className="text-sm font-semibold text-zinc-800">Convert Script</h4>
              </div>
              <p className="text-xs text-zinc-500">Paste an existing script and rewrite it in {inf.name}'s tone and style</p>
              <Textarea
                value={script}
                onChange={e => setScript(e.target.value)}
                placeholder="Paste your script here..."
                rows={4}
                className="bg-white border-zinc-200 text-zinc-900 text-sm"
                disabled={converting}
                data-testid="script-input"
              />
              <Button onClick={handleConvert} disabled={converting || !script.trim()} className="w-full bg-zinc-900 text-white hover:bg-zinc-800" data-testid="convert-script-btn">
                {converting ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Converting...</> : <><Zap size={14} className="mr-1.5" /> Convert Script</>}
              </Button>
            </div>

            {/* Generate from idea */}
            <div className="border border-zinc-200 rounded-lg p-4 space-y-3 bg-amber-50/30">
              <div className="flex items-center gap-2">
                <Lightbulb size={16} className="text-amber-600" />
                <h4 className="text-sm font-semibold text-zinc-800">Idea → Full Script</h4>
              </div>
              <p className="text-xs text-zinc-500">Describe an idea + upload reference images (chats, renders, etc.) and get a full production-ready script</p>
              <Button onClick={() => setShowIdeaDialog(true)} className="w-full bg-amber-600 text-white hover:bg-amber-700" data-testid="open-idea-dialog-btn">
                <Lightbulb size={14} className="mr-1.5" /> Generate Script from Idea
              </Button>
            </div>
          </div>

          {/* Convert Result */}
          {convertedResult && (
            <div className="mt-5 bg-blue-50 border border-blue-200 rounded-lg p-5" data-testid="converted-result">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-blue-800 uppercase tracking-wider">Converted Script — {convertedResult.influencer_name}'s Style</span>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-blue-700" onClick={() => copyToClipboard(convertedResult.converted_script)}><Copy size={12} className="mr-1" /> Copy</Button>
              </div>
              <div className="text-sm text-blue-900 leading-relaxed whitespace-pre-wrap">{convertedResult.converted_script}</div>
            </div>
          )}

          {/* Generated Result */}
          {generatedResult && (
            <div className="mt-5 bg-amber-50 border border-amber-200 rounded-lg p-5" data-testid="generated-result">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Generated Script — {generatedResult.influencer_name}'s Style {generatedResult.image_count > 0 && `(${generatedResult.image_count} images used)`}</span>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-700" onClick={() => copyToClipboard(generatedResult.generated_script)}><Copy size={12} className="mr-1" /> Copy</Button>
              </div>
              <div className="text-sm text-amber-900 leading-relaxed whitespace-pre-wrap">{generatedResult.generated_script}</div>
            </div>
          )}
        </Section>
      )}

      {/* Conversion History */}
      {inf.conversion_history?.length > 0 && (
        <Section title="Script Conversion History" subtitle={`${inf.conversion_history.length} conversions`} icon={<FileText size={16} />}>
          <button onClick={() => setShowConvertHistory(!showConvertHistory)} className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900" data-testid="toggle-convert-history">
            {showConvertHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {showConvertHistory ? 'Hide' : 'Show'} History
          </button>
          {showConvertHistory && (
            <div className="mt-3 space-y-4 max-h-[500px] overflow-y-auto">
              {inf.conversion_history.slice().reverse().map(h => (
                <div key={h.id} className="border border-zinc-200 rounded-md overflow-hidden">
                  <div className="bg-zinc-50 px-4 py-2 border-b border-zinc-200 flex items-center justify-between">
                    <span className="text-xs text-zinc-500">{new Date(h.created_at).toLocaleString('en-IN')}</span>
                    <Button variant="ghost" size="sm" className="h-6 text-xs text-zinc-400" onClick={() => copyToClipboard(h.converted_script)}><Copy size={12} className="mr-1" /> Copy</Button>
                  </div>
                  <div className="grid md:grid-cols-2 divide-x divide-zinc-200">
                    <div className="p-3"><p className="text-xs font-medium text-zinc-500 mb-1">Original</p><p className="text-xs text-zinc-700 whitespace-pre-wrap line-clamp-6">{h.original_script}</p></div>
                    <div className="p-3"><p className="text-xs font-medium text-blue-600 mb-1">Converted</p><p className="text-xs text-zinc-700 whitespace-pre-wrap line-clamp-6">{h.converted_script}</p></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Generation History */}
      {inf.generation_history?.length > 0 && (
        <Section title="Idea → Script History" subtitle={`${inf.generation_history.length} generations`} icon={<Lightbulb size={16} />}>
          <button onClick={() => setShowGenHistory(!showGenHistory)} className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900" data-testid="toggle-gen-history">
            {showGenHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {showGenHistory ? 'Hide' : 'Show'} History
          </button>
          {showGenHistory && (
            <div className="mt-3 space-y-4 max-h-[500px] overflow-y-auto">
              {inf.generation_history.slice().reverse().map(h => (
                <div key={h.id} className="border border-amber-200 rounded-md overflow-hidden">
                  <div className="bg-amber-50 px-4 py-2 border-b border-amber-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-amber-700">{new Date(h.created_at).toLocaleString('en-IN')}</span>
                      {h.image_count > 0 && <Badge variant="outline" className="text-xs text-amber-600 border-amber-200">{h.image_count} images</Badge>}
                    </div>
                    <Button variant="ghost" size="sm" className="h-6 text-xs text-amber-600" onClick={() => copyToClipboard(h.generated_script)}><Copy size={12} className="mr-1" /> Copy</Button>
                  </div>
                  <div className="p-3 border-b border-amber-100">
                    <p className="text-xs font-medium text-zinc-500 mb-1">Idea</p>
                    <p className="text-xs text-zinc-700 whitespace-pre-wrap">{h.idea}</p>
                  </div>
                  <div className="p-3">
                    <p className="text-xs font-medium text-amber-700 mb-1">Generated Script</p>
                    <p className="text-xs text-zinc-700 whitespace-pre-wrap line-clamp-10">{h.generated_script}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Idea → Script Dialog */}
      <Dialog open={showIdeaDialog} onOpenChange={setShowIdeaDialog}>
        <DialogContent className="sm:max-w-xl bg-white text-zinc-900 max-h-[90vh] overflow-y-auto" data-testid="idea-to-script-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
              <Lightbulb size={18} className="text-amber-600" />
              Generate Script from Idea
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-500 -mt-1">
            Describe your content idea and upload reference images. The system will generate a complete, production-ready script in <strong>{inf.name}'s</strong> style — including creative embellishments and virality boosters.
          </p>

          <div className="space-y-4 mt-3">
            {/* Idea text */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">Your Idea</label>
              <Textarea
                value={ideaText}
                onChange={e => setIdeaText(e.target.value)}
                placeholder="e.g. Custom ring process video — show the customer chat where they describe what they want, then the 3D render we made, then the final ring reveal. The customer wanted a unique emerald cut with a hidden halo..."
                rows={5}
                className="bg-white border-zinc-200 text-zinc-900 text-sm"
                disabled={generating}
                data-testid="idea-text-input"
              />
            </div>

            {/* Image upload */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Reference Images <span className="text-zinc-400 font-normal">(optional, up to 5)</span>
              </label>
              <p className="text-xs text-zinc-400 mb-2">Upload screenshots of customer chats, ring renders, behind-the-scenes photos, or any visual reference</p>
              <input type="file" ref={ideaImageRef} className="hidden" accept="image/*" multiple onChange={handleAddIdeaImages} />
              <Button variant="outline" onClick={() => ideaImageRef.current?.click()} disabled={ideaImages.length >= 5 || generating} className="text-zinc-700 border-zinc-200 mb-2" data-testid="upload-idea-images-btn">
                <Image size={14} className="mr-1.5" /> Add Images ({ideaImages.length}/5)
              </Button>

              {ideaImages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {ideaImages.map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={URL.createObjectURL(img)} alt={img.name} className="w-16 h-16 object-cover rounded-md border border-zinc-200" />
                      <button
                        onClick={() => removeIdeaImage(i)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={10} />
                      </button>
                      <p className="text-[10px] text-zinc-400 truncate w-16 mt-0.5">{img.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Generate button */}
            <Button onClick={handleGenerateScript} disabled={generating || !ideaText.trim()} className="w-full bg-amber-600 text-white hover:bg-amber-700 py-2.5" data-testid="generate-script-btn">
              {generating ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> Generating full script...</>
              ) : (
                <><Sparkles size={14} className="mr-1.5" /> Generate Script in {inf.name}'s Style</>
              )}
            </Button>

            {generating && (
              <p className="text-xs text-zinc-400 text-center">
                Analyzing your idea and images, building narrative in {inf.name}'s style... this may take 30-60 seconds
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, subtitle, icon, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid={`section-${title.toLowerCase().replace(/[\s/:→]/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-zinc-500">{icon}</span>
        <h3 className="text-sm font-semibold text-zinc-800">{title}</h3>
      </div>
      {subtitle && <p className="text-xs text-zinc-400 mb-4 ml-6">{subtitle}</p>}
      {children}
    </div>
  );
}
